import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TransferAccountStep } from '../TransferAccount.meta';
import { StatusScreen } from '../../StatusScreen/StatusScreen';
import { useValidateAccountTransferMutation } from '../../../api/useValidateAccountTransferMutation';
import { useTransferMoneyContext } from '../../../hooks/useTransferMoneyContext';
import { TransferAccountDTO } from '../../../api/useTransferAccountsOptionsQuery';
import { formatCurrency } from '../../../../../CommonUtils/formatNumber';
import { ModalHeader } from '../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { Modal } from '../../../../../CommonComponents/OPL/Modal/Modal';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { Radio } from '../../../../../CommonComponents/OPL/Radio/Radio';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';

interface Props {
  onClose(): void;
  setStep(step: TransferAccountStep): void;
  selectedAccount: TransferAccountDTO;
}

interface FormValues {
  accountTypeMatches: boolean;
}

export const TransferAccountConfirmType: React.FC<Props> = ({
  onClose,
  setStep,
  selectedAccount,
}) => {
  const formBag = useForm<FormValues>({
    defaultValues: {
      accountTypeMatches: true,
    },
  });

  const [showWarningScreen, setShowWarningScreen] = useState(false);
  const [showAccountTypeErrorScreen, setShowAccountTypeErrorScreen] = useState(false);
  const [showInstitutionErrorScreen, setShowInstitutionErrorScreen] = useState(false);

  const { clientId, managedAccountId } = useTransferMoneyContext();

  const {
    mutateAsync: validateAccountTransfer,
    isLoading: isValidating,
    error: validationError,
  } = useValidateAccountTransferMutation();

  const onSubmit = async (values: FormValues) => {
    if (values.accountTypeMatches) {
      try {
        await validateAccountTransfer({
          clientId,
          managedAccountId,
          fromAccountId: selectedAccount.id,
        });

        setStep(TransferAccountStep.TransferAccountConfirmNumber);
      } catch (err) {
        console.error(err);

        if (err instanceof Error && err.message === 'Account types do not match') {
          setShowAccountTypeErrorScreen(true);
        } else {
          setShowInstitutionErrorScreen(true);
        }
      }
    } else {
      setShowWarningScreen(true);
    }
  };

  if (showWarningScreen) {
    return (
      <StatusScreen
        title="Verify Account Type"
        status="warning"
        action={{
          label: 'Update Account Type',
          onClick() {
            ADV.util.Router.go2('ProfileAccounts', {
              clientId,
              tabSlug: 'preferences',
              entityId: selectedAccount.id,
            });
          },
        }}
        onClose={onClose}
      >
        If the external account type is incorrect, you may update it by editing your account
        details.
      </StatusScreen>
    );
  }

  if (showAccountTypeErrorScreen || showInstitutionErrorScreen) {
    return (
      <StatusScreen
        title="ACAT Transfer Not Supported"
        status="error"
        action={{
          label: 'Select Another Account',
          onClick() {
            setStep(TransferAccountStep.TransferAccount);
          },
        }}
        onClose={onClose}
      >
        {showInstitutionErrorScreen && (
          <>
            <Text fontWeight={600} color="neutral.600" as="span">
              {selectedAccount.institutionName}
            </Text>
            &nbsp;doesn&apos;t support this type of transfers. You may need to reach out to your
            institution to initiate a transfer of funds.
          </>
        )}

        {showAccountTypeErrorScreen && (
          <>
            Your&nbsp;
            <Text fontWeight={600} color="neutral.600" as="span">
              {selectedAccount.accountType}
            </Text>
            &nbsp;doesn&apos;t support transfers from that external account type.
          </>
        )}
      </StatusScreen>
    );
  }

  return (
    <Modal isOpen onRequestClose={onClose}>
      <ModalHeader>Confirm Account Type</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      {validationError && (
        <Alert message={validationError.message} variant="error" my="16" mx="24" />
      )}

      <form onSubmit={formBag.handleSubmit(onSubmit)}>
        <ModalBody>
          Please confirm the external account type is correct. We need this to ensure a successful
          transfer.
          <Text variant="smallCaps" mt="24" fontWeight={600}>
            from
          </Text>
          <Text fontWeight={500} fontSize="h6">
            {selectedAccount.accountType}
          </Text>
          <Box display="flex" justifyContent="space-between" mb="24">
            <Text color="neutral.400">{selectedAccount.name}</Text>
            <Text color="neutral.500">{formatCurrency(selectedAccount.balance)}</Text>
          </Box>
          <Box display="flex" flexDirection="column">
            <Controller
              control={formBag.control}
              name="accountTypeMatches"
              render={({ field: { value } }) => {
                return (
                  <Box display="flex" flexDirection="column">
                    <Radio
                      checked={value}
                      onChange={() => formBag.setValue('accountTypeMatches', true)}
                      mb="8"
                    >
                      Yes
                    </Radio>
                    <Radio
                      checked={!value}
                      onChange={() => formBag.setValue('accountTypeMatches', false)}
                    >
                      No
                    </Radio>
                  </Box>
                );
              }}
            />
          </Box>
        </ModalBody>

        <ModalFooter>
          <Box flex={1}>
            <Button
              leftIcon="fas fa-chevron-left"
              variant="secondary"
              onClick={() => setStep(TransferAccountStep.TransferAccount)}
            >
              Back
            </Button>
          </Box>

          <Button variant="subtleInactive" onClick={onClose} mr="8">
            Cancel
          </Button>

          <Button type="submit" isLoading={isValidating}>
            Next
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
